import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const DATE_TYPES = [
  { value: 'custom', label: 'Custom date range' },
  { value: 'selection', label: 'Date selection' },
] as const;
export const DATE_CADENCES = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'mtd', label: 'Monthly - MTD' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Yearly' },
  { value: 'ytd', label: 'Yearly - YTD' },
] as const;
export const CUSTOM_COMPARISONS = [
  { value: 'previous', label: 'Previous Period' },
  { value: 'month', label: 'Previous Month' },
  { value: 'quarter', label: 'Previous Quarter' },
  { value: 'year', label: 'Previous Year' },
] as const;
export const SELECTION_COMPARISONS = {
  day: [{ value: 'day', label: 'DoD Day Prior' }],
  week: [{ value: 'week', label: 'WoW Week Prior' }],
  month: [
    { value: 'month', label: 'MoM Last Month' },
    { value: 'year', label: 'MoM Same Month LY' },
  ],
  mtd: [{ value: 'month', label: 'MoM MTD Last Month' }],
  quarter: [
    { value: 'quarter', label: 'QoQ Last Quarter' },
    { value: 'year', label: 'QoQ Same Quarter LY' },
  ],
  year: [{ value: 'year', label: 'YoY Last Year' }],
  ytd: [{ value: 'year', label: 'YoY YTD Last Year' }],
} as const;

export type DateCadence = (typeof DATE_CADENCES)[number]['value'];
export type SelectionComparison = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type AgentHubPeriodInput = {
  dateType: (typeof DATE_TYPES)[number]['value'];
  from: string;
  to: string;
  dateSelection: string;
  dateCadence: DateCadence;
  customComparison: (typeof CUSTOM_COMPARISONS)[number]['value'];
  selectionComparison: SelectionComparison;
};
export type DateRange = { from: string; to: string };

export function selectionComparisonOf(
  input: Pick<AgentHubPeriodInput, 'dateCadence' | 'selectionComparison'>,
): SelectionComparison {
  const choices: ReadonlyArray<{ value: SelectionComparison }> = SELECTION_COMPARISONS[input.dateCadence];
  return choices.find((choice) => choice.value === input.selectionComparison)?.value ?? choices[0].value;
}

export const chartCadenceOf = (input: Pick<AgentHubPeriodInput, 'dateCadence'>) =>
  input.dateCadence === 'mtd' ? 'month' : input.dateCadence === 'ytd' ? 'year' : input.dateCadence;

/** Dates are Sydney civil days. UTC arithmetic avoids host timezone and DST changing their shape. */
export function resolveAgentHubPeriod(input: AgentHubPeriodInput): { current: DateRange; comparison: DateRange } {
  const format = (date: dayjs.Dayjs) => date.format('YYYY-MM-DD');
  const shift = (date: dayjs.Dayjs, unit: SelectionComparison) =>
    unit === 'quarter' ? date.subtract(3, 'month') : date.subtract(1, unit);
  if (input.dateType === 'custom') {
    const start = dayjs.utc(input.from);
    const days = dayjs.utc(input.to).diff(start, 'day');
    const prior =
      input.customComparison === 'previous' ? start.subtract(days + 1, 'day') : shift(start, input.customComparison);
    return {
      current: { from: input.from, to: input.to },
      comparison: { from: format(prior), to: format(prior.add(days, 'day')) },
    };
  }
  const selected = dayjs.utc(input.dateSelection);
  const cadence = input.dateCadence;
  const range = (date: dayjs.Dayjs): DateRange => {
    let start = date;
    let end = date;
    if (cadence === 'week') {
      start = date.subtract((date.day() + 6) % 7, 'day');
      end = start.add(6, 'day');
    } else if (cadence === 'quarter') {
      start = date.month(Math.floor(date.month() / 3) * 3).startOf('month');
      end = start.add(3, 'month').subtract(1, 'day');
    } else if (cadence !== 'day') {
      const unit = cadence === 'mtd' || cadence === 'month' ? 'month' : 'year';
      start = date.startOf(unit);
      end = cadence === 'mtd' || cadence === 'ytd' ? date : date.endOf(unit);
    }
    return { from: format(start), to: format(end) };
  };
  return { current: range(selected), comparison: range(shift(selected, selectionComparisonOf(input))) };
}
